import JSZip from 'jszip';
import imageSize from 'browser-image-size';

const DIR_KEY_4P = '4P';
const DIR_KEY_38M = '38M';

const base: Record<string, Record<string, { width: number; height: number }>> = {
	'3P': {
		'board_path_blue.webp': { width: 1247, height: 1102 },
		'board_path_border.webp': { width: 1335, height: 1191 },
		'board_path_green.webp': { width: 1247, height: 1102 },
		'board_path_red.webp': { width: 1247, height: 1102 },
		'board_path_yellow.webp': { width: 1247, height: 1102 },
		'board_shadow.webp': { width: 1344, height: 1178 },
		'score_blue_bg.png': { width: 240, height: 240 },
		'score_blue_glow_bg.png': { width: 240, height: 240 },
		'score_green_bg.png': { width: 240, height: 240 },
		'score_green_glow_bg.png': { width: 240, height: 240 },
		'score_red_bg.png': { width: 240, height: 240 },
		'score_red_glow_bg.png': { width: 240, height: 240 },
		'score_yellow_bg.png': { width: 240, height: 240 },
		'score_yellow_glow_bg.png': { width: 240, height: 240 }
	},
	'.': {
		'token_blue.png': { width: 45, height: 81 },
		'token_green.png': { width: 45, height: 81 },
		'token_red.png': { width: 45, height: 81 },
		'token_yellow.png': { width: 45, height: 81 },
		'destiny_blue.png': { width: 25, height: 25 },
		'destiny_empty.png': { width: 25, height: 25 },
		'destiny_green.png': { width: 25, height: 25 },
		'destiny_red.png': { width: 25, height: 25 },
		'destiny_yellow.png': { width: 25, height: 25 },
		'gameplay_bg.webp': { width: 720, height: 1380 },
		'pricepot_bg.png': { width: 534, height: 136 },
		'countdown_bg.webp': { width: 720, height: 1280 },
		'winner_bg.webp': { width: 720, height: 1280 },
		'winner_crown_header.png': { width: 180, height: 114 },
		'countdown_waiting.webp': { width: 720, height: 730 },
		'revenge_bg.webp': { width: 720, height: 1280 }
	},
	'4P': {
		'board_bg.webp': { width: 720, height: 840 },
		'board_bg_neutral.webp': { width: 720, height: 840 },
		'board_path.webp': { width: 684, height: 684 },
		'home_bg.png': { width: 134, height: 134 },
		'home_icon.png': { width: 134, height: 134 },
		'safe_spot.png': { width: 42, height: 42 },
		'score_blue_bg.png': { width: 240, height: 240 },
		'score_blue_glow_bg.png': { width: 240, height: 240 },
		'score_green_bg.png': { width: 240, height: 240 },
		'score_green_glow_bg.png': { width: 240, height: 240 },
		'score_red_bg.png': { width: 240, height: 240 },
		'score_red_glow_bg.png': { width: 240, height: 240 },
		'score_yellow_bg.png': { width: 240, height: 240 },
		'score_yellow_glow_bg.png': { width: 240, height: 240 }
	},
	'38M': {
		'board_bg_neutral.webp': { width: 720, height: 840 },
		'board_bg.webp': { width: 720, height: 840 },
		'board_path.webp': { width: 684, height: 684 },
		'safe_spot.png': { width: 42, height: 42 }
	}
};

function isOptional(file: string, dirKey: string) {
	return ['safe_spot.png'].includes(file) && [DIR_KEY_4P, DIR_KEY_38M].includes(dirKey);
}

function detectThemeDirectory(zip: JSZip): string {
	const allPaths: string[] = [];
	zip.forEach((relativePath) => {
		if (!relativePath.startsWith('__MACOSX/') && !relativePath.includes('.DS_Store')) {
			allPaths.push(relativePath);
		}
	});

	if (allPaths.length === 0) {
		return '';
	}

	const firstPath = allPaths[0];
	const firstSlashIndex = firstPath.indexOf('/');

	if (firstSlashIndex === -1) {
		return '';
	}

	const possibleRootDir = firstPath.substring(0, firstSlashIndex + 1);

	const allFilesInRootDir = allPaths.every((path) => path.startsWith(possibleRootDir));

	if (allFilesInRootDir) {
		return possibleRootDir.substring(0, possibleRootDir.length - 1);
	}

	return '';
}

function findMatchingFiles(zip: JSZip, dirKey: string, fileName: string, themeDir: string): JSZip.JSZipObject[] {
	const matches: JSZip.JSZipObject[] = [];
	const lowerCaseFileName = fileName.toLowerCase();
	const lowerCaseDirKey = dirKey.toLowerCase();
	const pattern = dirKey === '.' ? lowerCaseFileName : `${lowerCaseDirKey}/${lowerCaseFileName}`;
	const lowerCaseThemeDir = themeDir.toLowerCase();

	zip.forEach((relativePath, file) => {
		const lowerCaseRelativePath = relativePath.toLowerCase();

		if (lowerCaseRelativePath.startsWith('__macosx/') || lowerCaseRelativePath.includes('.ds_store')) {
			return;
		}

		if (lowerCaseThemeDir && lowerCaseRelativePath === `${lowerCaseThemeDir}/${pattern}`) {
			matches.push(file);
		} else if (lowerCaseThemeDir && dirKey === '.' && lowerCaseRelativePath === `${lowerCaseThemeDir}/${lowerCaseFileName}`) {
			matches.push(file);
		} else if (lowerCaseRelativePath === pattern) {
			matches.push(file);
		} else if (dirKey === '.' && lowerCaseRelativePath === lowerCaseFileName) {
			matches.push(file);
		} else if (lowerCaseRelativePath.endsWith(`/${lowerCaseFileName}`)) {
			if (dirKey !== '.' && lowerCaseRelativePath.includes(`/${lowerCaseDirKey}/`)) {
				matches.push(file);
			}
		}
	});

	return matches;
}

async function validateFileEntry(
	zip: JSZip,
	dirKey: string,
	fileName: string,
	expectedSize: { width: number; height: number },
	themeDir: string,
	isFileOptional: boolean
): Promise<string | null> {
	const matchingFiles = findMatchingFiles(zip, dirKey, fileName, themeDir);
	const dirContext = dirKey === '.' ? 'root directory' : `directory: ${dirKey}`;

	if (matchingFiles.length === 0) {
		if (isFileOptional) {
			return null;
		}
		return `- Missing file: ${fileName} (in ${dirContext})`;
	}
	const file = matchingFiles[0];

	try {
		const fileData = await file.async('blob');
		if (!fileData || fileData.size === 0) {
			return `- Empty file: ${fileName} (in ${dirContext})`;
		}

		if (fileName.endsWith('.webp')) {
			let fileSignature: Uint8Array = await file.async('uint8array');
			fileSignature = fileSignature.slice(0, 12);
			const signatureStr = Array.from(fileSignature)
				.map((byte) => String.fromCharCode(byte))
				.join('');

			if (signatureStr.startsWith('\xFF\xD8\xFF')) {
				return `- File ${fileName} has a .webp extension but appears to be a JPEG file.`;
			}
			if (!signatureStr.startsWith('RIFF') || !signatureStr.includes('WEBP', 4)) {
				return `- File ${fileName} has a .webp extension but does not have a valid WebP signature.`;
			}
		}

		if (fileName.endsWith('.webp') || fileName.endsWith('.png')) {
			try {
				const { width, height } = await imageSize(fileData);
				if (width * expectedSize.height !== expectedSize.width * height) {
					return `- Incorrect aspect ratio for ${fileName} (in ${dirContext}). Image: ${width}x${height}, Expected ratio from: ${expectedSize.width}x${expectedSize.height}.`;
				}

				if (expectedSize.width === 0 && width !== 0) {
					return `- Image width (${width}) for ${fileName} (in ${dirContext}) must be 0 as expected base width is 0.`;
				}

				if (expectedSize.height === 0 && height !== 0) {
					return `- Image height (${height}) for ${fileName} (in ${dirContext}) must be 0 as expected base height is 0.`;
				}
			} catch (imageSizeError: any) {
				return `- Could not determine dimensions for ${fileName} (in ${dirContext}): ${
					imageSizeError?.message || 'Unknown error'
				}`;
			}
		}
	} catch (error: any) {
		return `- Error processing file ${fileName} (in ${dirContext}): ${error?.message || 'Unknown error'}`;
	}

	return null;
}

export async function validateThemeZipBrowser(arrayBuffer: ArrayBuffer): Promise<{ success: boolean; issues: string[] }> {
	const allIssues: string[] = [];
	let zip: JSZip;

	try {
		zip = await JSZip.loadAsync(arrayBuffer);
	} catch (err) {
		return { success: false, issues: ['Failed to read ZIP file.'] };
	}

	const themeDir = detectThemeDirectory(zip);

	let hasOuterShellError = false;
	if (themeDir) {
		hasOuterShellError = true;
	}

	const dirKeys = Object.keys(base);
	const allFileValidationPromises: Promise<string | null>[] = [];

	for (let i = 0; i < dirKeys.length; i += 1) {
		const dirKey = dirKeys[i];
		const expectedFiles = base[dirKey];

		if (dirKey === DIR_KEY_4P || dirKey === DIR_KEY_38M) {
			const boardBgFiles = findMatchingFiles(zip, dirKey, 'board_bg.webp', themeDir);
			const boardBgNeutralFiles = findMatchingFiles(zip, dirKey, 'board_bg_neutral.webp', themeDir);

			if (boardBgFiles.length > 0) {
				allFileValidationPromises.push(
					validateFileEntry(zip, dirKey, 'board_bg.webp', expectedFiles['board_bg.webp'], themeDir, false)
				);
			}
			if (boardBgNeutralFiles.length > 0) {
				allFileValidationPromises.push(
					validateFileEntry(zip, dirKey, 'board_bg_neutral.webp', expectedFiles['board_bg_neutral.webp'], themeDir, false)
				);
			}

			if (boardBgFiles.length === 0 && boardBgNeutralFiles.length === 0) {
				allIssues.push(`- Missing board background in ${dirKey}. Need either board_bg_neutral.webp or board_bg.webp.`);
			}
		}

		let fileNames = Object.keys(expectedFiles);

		if (dirKey === DIR_KEY_4P || dirKey === DIR_KEY_38M) {
			fileNames = fileNames.filter((fn) => fn !== 'board_bg.webp' && fn !== 'board_bg_neutral.webp');
		}

		for (let j = 0; j < fileNames.length; j += 1) {
			const fileName = fileNames[j];
			const expectedSize = expectedFiles[fileName];
			const isOptionalFile = isOptional(fileName, dirKey);

			allFileValidationPromises.push(validateFileEntry(zip, dirKey, fileName, expectedSize, themeDir, isOptionalFile));
		}
	}

	const results = await Promise.all(allFileValidationPromises);
	results.forEach((issue) => {
		if (issue) {
			allIssues.push(issue);
		}
	});

	if (hasOuterShellError) {
		allIssues.push(
			`- Invalid ZIP structure: All files are nested inside an unnecessary outer directory '${themeDir}'. Please re-zip without the containing folder.`
		);
	}

	return { success: allIssues.length === 0, issues: allIssues };
}
